// Diagramma schematico animato FTTH/FTTC/FWA — porting da docs/design-reference/new-website-v2/project/diagrammi.jsx (DirA "line-art").
// Le keyframes (dgm-flow, dgm-wave, dgm-pulse) vivono in design.css.
// Palette/temi in ./diagramTheme; i glifi line-art (Casa/FiberLink/SegLabel/NodeLabel)
// sono esportati per riuso da altri diagrammi schematici (es. IpSchema).

import { PAL, DARK, LIGHT, type Theme } from './diagramTheme'

export type TechKind = 'ftth' | 'fttc' | 'fwa'

function Centrale({ t, sw = 2 }: { t: Theme; sw?: number }) {
  return (
    <g fill="none" stroke={t.stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round">
      <rect x={-22} y={-28} width={44} height={56} rx={4} fill={t.fillSoft} />
      <line x1={-22} y1={-11} x2={22} y2={-11} stroke={t.dim} />
      <line x1={-22} y1={6} x2={22} y2={6} stroke={t.dim} />
      <rect x={-15} y={-23} width={7} height={7} rx={1.5} stroke={t.dim} />
      <rect x={-3.5} y={-23} width={7} height={7} rx={1.5} stroke={t.dim} />
      <rect x={8} y={-23} width={7} height={7} rx={1.5} stroke={t.dim} />
      <rect x={-15} y={-6} width={7} height={7} rx={1.5} stroke={t.dim} />
      <rect x={8} y={-6} width={7} height={7} rx={1.5} stroke={t.dim} />
      <rect x={-7} y={13} width={14} height={15} rx={1.5} />
      <rect x={-9} y={-36} width={18} height={9} rx={2} fill={t.fillSoft} />
      <circle cx={0} cy={-31.5} r={1.8} fill={PAL.cyan} stroke="none" />
    </g>
  )
}

export function Casa({ t, sw = 2, cpe = false, accent = PAL.cyan }: { t: Theme; sw?: number; cpe?: boolean; accent?: string }) {
  return (
    <g fill="none" stroke={t.stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round">
      <path d="M-26 -2 L0 -24 L26 -2" fill={t.fillSoft} />
      <rect x={-20} y={-2} width={40} height={30} rx={2} fill={t.fillSoft} />
      <rect x={-6} y={12} width={12} height={16} rx={1.5} />
      <rect x={8} y={4} width={9} height={9} rx={1} stroke={t.dim} />
      <rect x={-17} y={4} width={9} height={9} rx={1} stroke={t.dim} />
      {cpe && (
        <g>
          <rect x={9} y={-26} width={11} height={8} rx={1.5} stroke={accent} fill="none" />
          <line x1={14.5} y1={-26} x2={14.5} y2={-31} stroke={accent} />
          <circle cx={14.5} cy={-32} r={1.6} fill={accent} stroke="none" />
        </g>
      )}
    </g>
  )
}

function Armadio({ t, sw = 2 }: { t: Theme; sw?: number }) {
  return (
    <g fill="none" stroke={t.stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round">
      <rect x={-15} y={-26} width={30} height={50} rx={3} fill={t.fillSoft} />
      <line x1={0} y1={-26} x2={0} y2={24} stroke={t.dim} />
      <line x1={-10} y1={-20} x2={-3} y2={-20} stroke={t.dim} />
      <line x1={-10} y1={-15} x2={-3} y2={-15} stroke={t.dim} />
      <line x1={3} y1={-20} x2={10} y2={-20} stroke={t.dim} />
      <line x1={3} y1={-15} x2={10} y2={-15} stroke={t.dim} />
      <circle cx={-3} cy={2} r={1.6} fill={t.dim} stroke="none" />
      <circle cx={3} cy={2} r={1.6} fill={t.dim} stroke="none" />
      <line x1={-18} y1={24} x2={18} y2={24} strokeWidth={sw + 0.5} />
    </g>
  )
}

function Antenna({ t, sw = 2 }: { t: Theme; sw?: number }) {
  return (
    <g fill="none" stroke={t.stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round">
      <path d="M-13 28 L-5 -20" />
      <path d="M13 28 L5 -20" />
      <line x1={-10} y1={11} x2={10} y2={11} stroke={t.dim} />
      <line x1={-7.5} y1={-3} x2={7.5} y2={-3} stroke={t.dim} />
      <path d="M-10 11 L7.5 -3 M10 11 L-7.5 -3" stroke={t.dim} strokeWidth={sw - 0.4} />
      <rect x={-6} y={-30} width={12} height={11} rx={2} fill={t.fillSoft} />
      <line x1={-13} y1={28} x2={13} y2={28} strokeWidth={sw + 0.5} />
    </g>
  )
}

export function FiberLink({ idp, x1, x2, y, w = 4, dur = 2.0 }: { idp: string; x1: number; x2: number; y: number; w?: number; dur?: number }) {
  const d = `M${x1} ${y} L${x2} ${y}`
  const pid = `${idp}-fib`
  return (
    <g>
      <path id={pid} d={d} fill="none" stroke={`url(#${idp}-gFiber)`} strokeWidth={w} strokeLinecap="round" opacity="0.95" />
      <path d={d} fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth={w * 0.5} strokeLinecap="round"
        strokeDasharray="1.5 13" className="dgm-flow" />
      <g>
        <circle r={w * 1.9} fill="rgba(255,255,255,0.18)" />
        <circle r={w * 0.85} fill="#fff" />
        <animateMotion dur={`${dur}s`} repeatCount="indefinite" rotate="0">
          <mpath href={`#${pid}`} />
        </animateMotion>
      </g>
    </g>
  )
}

function CopperLink({ idp, x1, x2, y, w = 4, dur = 2.4 }: { idp: string; x1: number; x2: number; y: number; w?: number; dur?: number }) {
  const d = `M${x1} ${y} L${x2} ${y}`
  const pid = `${idp}-cop`
  return (
    <g>
      <path id={pid} d={d} fill="none" stroke={`url(#${idp}-gCopper)`} strokeWidth={w} strokeLinecap="round" opacity="0.95" />
      <path d={d} fill="none" stroke="rgba(255,224,178,0.95)" strokeWidth={w * 0.5} strokeLinecap="round"
        strokeDasharray="1.5 13" className="dgm-flow" />
      <g>
        <circle r={w * 1.9} fill="rgba(230,178,74,0.22)" />
        <circle r={w * 0.85} fill={PAL.gold} />
        <animateMotion dur={`${dur}s`} repeatCount="indefinite">
          <mpath href={`#${pid}`} />
        </animateMotion>
      </g>
    </g>
  )
}

function RadioLink({ x1, x2, y, accent = PAL.azure }: { x1: number; x2: number; y: number; accent?: string }) {
  const ex = x1 + 4
  const start = 14
  const end = (x2 - ex) - 10
  const step = 19
  const arcs: { r: number; i: number }[] = []
  for (let r = start, i = 0; r <= end; r += step, i++) arcs.push({ r, i })
  const span = 16
  return (
    <g fill="none" stroke={accent} strokeLinecap="round">
      {arcs.map(({ r, i }) => (
        <path key={i}
          d={`M${ex + r} ${y - span} Q${ex + r + 10} ${y} ${ex + r} ${y + span}`}
          strokeWidth={2.4}
          className="dgm-wave"
          style={{ animationDelay: `${(i % 4) * 0.28}s` }} />
      ))}
      <circle cx={ex} cy={y} r={3} fill={accent} stroke="none" />
      <circle cx={x2} cy={y} r={3.4} fill={accent} stroke="none" className="dgm-pulse" />
    </g>
  )
}

export function SegLabel({ cx, y, text, color, t }: { cx: number; y: number; text: string; color: string; t: Theme }) {
  const w = text.length * 6.6 + 18
  return (
    <g>
      <rect x={cx - w / 2} y={y - 10} width={w} height={20} rx={10}
        fill={t.fillSoft} stroke={color} strokeOpacity="0.5" strokeWidth="1" />
      <text x={cx} y={y + 4} textAnchor="middle"
        fontFamily="Montserrat, sans-serif" fontSize="10.5" fontWeight="800"
        letterSpacing="0.12em" fill={color}>{text}</text>
    </g>
  )
}

export function NodeLabel({ cx, y, top, sub, t }: { cx: number; y: number; top: string; sub?: string; t: Theme }) {
  return (
    <g>
      <text x={cx} y={y} textAnchor="middle" fontFamily="Montserrat, sans-serif"
        fontSize="12" fontWeight="800" letterSpacing="0.04em" fill={t.label}>{top}</text>
      {sub && (
        <text x={cx} y={y + 15} textAnchor="middle" fontFamily="Montserrat, sans-serif"
          fontSize="9.5" fontWeight="700" letterSpacing="0.14em" fill={t.sub}>{sub}</text>
      )}
    </g>
  )
}

export function TechDiagram({
  tech,
  light = false,
  bare = false,
}: { tech: TechKind; light?: boolean; bare?: boolean }) {
  const t = light ? LIGHT : DARK
  const W = 580
  const H = 300
  const y = 138
  const cxC = 78
  const cxMid = 290
  const cxHome = 502
  const idp = `td-${tech}-${light ? 'l' : 'd'}`
  const bg = light ? '#FFFFFF' : 'transparent'
  const border = light ? PAL.line : 'rgba(255,255,255,0.08)'
  const gridColor = light ? PAL.navy700 : '#fff'

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`${idp}-gFiber`} x1="0" y1="0" x2={W} y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={PAL.cyan} />
          <stop offset="25%" stopColor={PAL.azure} />
          <stop offset="55%" stopColor={PAL.violet} />
          <stop offset="82%" stopColor={PAL.magenta} />
          <stop offset="100%" stopColor="#FF7AC2" />
        </linearGradient>
        <linearGradient id={`${idp}-gCopper`} x1="0" y1="0" x2={W} y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={PAL.gold} />
          <stop offset="50%" stopColor="#C3623A" />
          <stop offset="100%" stopColor={PAL.gold} />
        </linearGradient>
      </defs>

      {!bare && <rect x="0" y="0" width={W} height={H} rx="20" fill={bg} />}
      {!bare && <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="19.5" fill="none" stroke={border} />}

      <g opacity={light ? 0.12 : 0.05} stroke={gridColor}>
        {Array.from({ length: 11 }).map((_, i) => <line key={`v${i}`} x1={i * 58} y1="0" x2={i * 58} y2={H} />)}
        {Array.from({ length: 6 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 58} x2={W} y2={i * 58} />)}
      </g>

      {tech === 'ftth' && <FiberLink idp={idp} x1={cxC + 30} x2={cxHome - 32} y={y} />}
      {tech === 'fttc' && (
        <>
          <FiberLink idp={idp} x1={cxC + 30} x2={cxMid - 22} y={y} />
          <CopperLink idp={idp} x1={cxMid + 22} x2={cxHome - 32} y={y} />
        </>
      )}
      {tech === 'fwa' && (
        <>
          <FiberLink idp={idp} x1={cxC + 30} x2={cxMid - 22} y={y} />
          <RadioLink x1={cxMid + 18} x2={cxHome - 30} y={y} accent={PAL.azure} />
        </>
      )}

      <g transform={`translate(${cxC},${y})`}><Centrale t={t} /></g>
      {tech === 'fttc' && <g transform={`translate(${cxMid},${y})`}><Armadio t={t} /></g>}
      {tech === 'fwa' && <g transform={`translate(${cxMid},${y - 4})`}><Antenna t={t} /></g>}
      <g transform={`translate(${cxHome},${y})`}><Casa t={t} cpe={tech === 'fwa'} /></g>

      {tech === 'ftth' && <SegLabel cx={(cxC + cxHome) / 2} y={y - 34} text="FIBRA OTTICA" color={PAL.cyan} t={t} />}
      {tech === 'fttc' && (
        <>
          <SegLabel cx={(cxC + 30 + cxMid - 22) / 2} y={y - 34} text="FIBRA" color={PAL.cyan} t={t} />
          <SegLabel cx={(cxMid + 22 + cxHome - 32) / 2} y={y - 34} text="RAME" color={PAL.gold} t={t} />
        </>
      )}
      {tech === 'fwa' && (
        <>
          <SegLabel cx={(cxC + 30 + cxMid - 22) / 2} y={y - 34} text="FIBRA" color={PAL.cyan} t={t} />
          <SegLabel cx={(cxMid + 18 + cxHome) / 2} y={y - 34} text="RADIO" color={PAL.azure} t={t} />
        </>
      )}

      <NodeLabel cx={cxC} y={y + 60} top="CENTRALE" sub="SPADHAUSEN" t={t} />
      {tech === 'fttc' && <NodeLabel cx={cxMid} y={y + 56} top="ARMADIO" sub="STRADALE" t={t} />}
      {tech === 'fwa' && <NodeLabel cx={cxMid} y={y + 60} top="ANTENNA" sub="BTS / FWA" t={t} />}
      <NodeLabel cx={cxHome} y={y + 60} top="CASA TUA" sub={tech === 'ftth' ? 'MODULO OTTICO' : tech === 'fttc' ? 'MODEM VDSL' : 'MODEM RADIO'} t={t} />
    </svg>
  )
}
